import React from 'react'

import { Table, FormControl } from 'react-bootstrap'
import { isString, values, keyBy, flatten, difference, omit, isNumber, isNil, keys, sortBy, sum } from 'lodash'

import { 
  str2date, date2str, l, randstr,
  printMoney, round 
} from '../../lib/Tools.js'
import { getText, nameOrTitle } from '../lib/text'
import { calculateTripPriceFromBill, getTotalCommissions } from '../../lib/TripTools'
import HoverTip from '../components/HoverTip'

import { getBI } from './ReservationTools'

function sumOfTwoMaybe (a = 0, b = 0) { return (a||0) + (b||0) }

/**
 * Resumen de pagos
 */

export const PaymentsSumup = ({ trip }) => {

  const pagados = (trip.payments.map(x =>  x.eur) || [0])
  const recibidos = (trip.payments.filter(x => x.isReceived).map(x => x.receivedEur || 0) || [0])
  
  const totalPagado = pagados.length > 0 ? pagados.reduce(sumOfTwoMaybe) : 0
  const totalRecibido = recibidos.length > 0 ? recibidos.reduce(sumOfTwoMaybe) : 0
  
  const anyNotReceived = trip.payments.some(p => !p.isReceived)
  const somePayments = trip.payments.length > 0

  return (
    <Table bordered striped style={{ maxWidth: 380 }}>
      <thead>
        <tr>
          <th style={{ maxWidth: 140 }}>Pago</th>
          <th style={{ width: 80 }}>Fecha</th>
          <th style={{ width: 80 }}>Recibido</th>
          <th style={{ width: 80 }}>Pagado</th>
        </tr>
      </thead>
      <tbody>
        {
          trip.payments.map(({ key, sentDate, name, eur, receivedEur, isReceived}) => (
            <tr key={key}>
              <td>{name}</td>
              <td>{sentDate}</td>
              <td style={{ textAlign: "center" }}>{isReceived ? printMoney(receivedEur) : "❌"}</td>
              <td style={{ textAlign: "center" }}>{printMoney(eur)}</td>
            </tr>
          ))
        }
        <tr>
          <td style={{fontWeight: "bold"}}>Total: </td>
          <td />
          <td style={{ textAlign: "center" }}>{printMoney(totalRecibido)}</td>
          <td style={{ textAlign: "center" }}>{printMoney(totalPagado)}</td>
        </tr>
      </tbody>
    </Table>
  )
}

/**
 * Resument de beneficios
 */

const SumupRow = ({ row, trip, editTrip }) => {
  const { k, title, value, isShowed, fw, tooltip, formKey } = row
  
  let v = value

  if (formKey) {
    v = (
      <span style={{ float: "right" }}>
        <FormControl
          style={{ display: "inline", width: 100, textAlign: "right" }}
          bsSize="small"
          value={trip[formKey]}
          type="number"
          onChange={evt => {
            // #TODO allow 0, or allow to delete the last digit
            const eur = Number.parseFloat(evt.target.value || 0) || 0
            if (isNumber(eur) && eur >= 0) { 
              editTrip({ [formKey]: eur })
            } 
          }}
        />
        {" €"}
      </span>
    )
  }
  else if (isNumber(value)) {
    v = printMoney(value)
  }

  return (
    <tr>
      <td style={{ width: 200, fontWeight: fw }}>{getText(title)}</td>
      <td style={{width: 200, textAlign: "right"}}>
        <HoverTip text={tooltip}><span style={{ fontWeight: fw}}>{v}</span></HoverTip>
      </td>
    </tr>
  )
}

export const Sumup = ({ trip, reservations, bill, editTrip, isDetailed }) => {

  const reservationsList = values(reservations)
  
  const presupuesto = calculateTripPriceFromBill(trip, bill).finalE
  
  const costesSinIVA = reservationsList.map(r => getBI(r))
  const costesIVA = reservationsList.map(r => r.finalPrice)
  const ivaSoportado = reservationsList.map(r => r.finalPrice - getBI(r)).reduce(sumOfTwoMaybe, 0) 

  const costesYaPagados = reservationsList.filter(r => r.status == "pag").map(r => r.finalPrice).reduce(sumOfTwoMaybe, 0)

  const cobros = trip.payments.filter(x => x.isReceived).map(x => x.receivedEur || 0)
  const pagados = trip.payments.map(x =>  x.eur) || 0
  const totalPagado = pagados.length > 0 ? pagados.reduce(sumOfTwoMaybe) : 0
  const perdido = trip.payments.filter(x => x.isReceived).map(x => x.eur - x.receivedEur || 0).reduce(sumOfTwoMaybe, 0)

  const costeTotalSinIVA = costesSinIVA.length > 0 ? costesSinIVA.reduce(sumOfTwoMaybe) : 0
  const costeTotalIVA = costesIVA.length > 0 ? costesIVA.reduce(sumOfTwoMaybe) : 0
  
  const totalCobrado = cobros.length > 0 ? cobros.reduce(sumOfTwoMaybe) : 0

  const exemptMoney = trip.exemptMoney + (trip.otherExempt || 0)
  const presupuestoTasable = (presupuesto - exemptMoney)
  const salesBI = presupuestoTasable / 1.21
  const salesIVA = presupuestoTasable - salesBI
  
  //#TODO el calculo puede que no sea siempre al 21 (o que no este hardcodeado) ????
  const margen = exemptMoney + salesBI - costeTotalSinIVA 
  const commissionsTotal = getTotalCommissions(trip)
  const margenTotal = margen + commissionsTotal

  const margenC =  `parte exenta + BI venta - costes sin iva = ${salesBI} + ${exemptMoney}) - ${costeTotalSinIVA}` 
  const margenTC = `margen + comision = ${margen} + ${commissionsTotal}`

  const porcentaje = round(100 * margen / presupuesto, 0.01)
  const porcentajeTotal = round(100 * margenTotal / presupuesto, 0.01)
  
  const balanceIVA = ivaSoportado - salesIVA

  const formulaBalance = (
    <div style={{ float: "right"}}>
      <span>{printMoney(ivaSoportado)} - </span>
      <span>{printMoney(salesIVA)} = </span>
      <span style={{ color: balanceIVA >= 0 ? "green" : "red" }}>
        {printMoney(balanceIVA) + "€"}
      </span>
    </div>)

  const baseEstimated = reservationsList.some(res => !res.baseImponible && res.finalPrice) // WARNING: assuming no base can be 0... dangerous
  const e = baseEstimated ? " (estimado)" : ""
  const d = isDetailed
  
  return (
    <Table bordered striped style={{ maxWidth: 400 }}>
      <tbody>
        {
          [
            { k: "pt",   title: "Presupuesto (PVP)",       formKey: null,          showed: true, fw: 400, tooltip: "",       value: `${presupuesto} €`                 },
            { k: "cyp",  title: "Costes ya pagados",       formKey: null,          showed: true, fw: 400, tooltip: "",       value: `${costesYaPagados} €`             },
            { k: "ccs",  title: `Precio coste (BI)${e}`,   formKey: null,          showed: d,    fw: 400, tooltip: "",       value: printMoney(costeTotalSinIVA) + `€` },
            { k: "cci",  title: "Precio coste (Con IVA)",  formKey: null,          showed: true, fw: 400, tooltip: "",       value: `${costeTotalIVA} €`               },
            { k: "tp",   title: "Pagado por el cliente",   formKey: null,          showed: d,    fw: 400, tooltip: "",       value: printMoney(totalPagado) + `€`      },
            { k: "lt",   title: "Comisión bancaria",       formKey: null,          showed: d,    fw: 400, tooltip: "",       value: printMoney(perdido) + `€`          },
            { k: "ex",   title: "Servicios CS exentos",    formKey: "exemptMoney", showed: d,    fw: 400, tooltip: "",       value: `${trip.exemptMoney} €`                 },
            { k: "ox",   title: "Otros exentos",           formKey: "otherExempt", showed: d,    fw: 400, tooltip: "",       value: `${trip.otherExempt} €`                 },
            { k: "chbi", title: "Venta BI",                formKey: null,          showed: d,    fw: 400, tooltip: "",       value: `${printMoney(salesBI)} €`         },
            { k: "chiv", title: "Venta IVA",               formKey: null,          showed: d,    fw: 400, tooltip: "",       value: `${printMoney(salesIVA)} €`         },
            { k: "b",    title: `Margen${e}`,              formKey: null,          showed: true, fw: 400, tooltip: margenC,  value: printMoney(margen) + `€`           },
            { k: "p",    title: `Margen %${e}`,            formKey: null,          showed: d,    fw: 400, tooltip: "",       value: `${porcentaje.toFixed(2)} %`      },
            { k: "cb",   title: "Comisiones y descuentos", formKey: null,          showed: true, fw: 400, tooltip: "",       value: `${commissionsTotal} €`            },
            { k: "bt",   title: `Margen total${e}`,        formKey: null,          showed: true, fw: 700, tooltip: margenTC, value: printMoney(margenTotal) + `€`      },
            { k: "pmt",  title: `Margen total %${e}`,      formKey: null,          showed: true, fw: 700, tooltip: "",       value: `${porcentajeTotal.toFixed(2)} %` },
            { k: "bi",   title: `Liquidación IVA${e}`,     formKey: null,          showed: d,    fw: 400, tooltip: "",       value: formulaBalance                     }                    
          ]
            .filter(row => row.showed)
            .map(row => (<SumupRow trip={trip} row={row} editTrip={editTrip} key={row.k} />))
        }
        {
          baseEstimated && (
            <tr>
              <td colSpan={2}>{getText("Faltan bases imponibles por indicar; los márgenes usan BIs estimadas.")}</td>
            </tr>
          )
        }
      </tbody>
    </Table>
  )
}
